import type { CombinationPreviewProps } from '../types';
import { MicroLabel, cn } from '../ui/primitives';

/**
 * What the combination on screen would total.
 *
 * A what-if, and labelled as one: the client makes the real picks. It exists
 * because an offer is a grid of possibilities — an admin authoring one has no
 * other way to ask "if they take 500 totes on express, what do we earn?" — and
 * it sits at the foot of the offer because that is the question the offer is
 * built to answer.
 */
export function CombinationPreview({
	selection,
	excludedNote,
	lines,
	subtotal,
	vatLabel = 'VAT',
	vat,
	total,
	totalNote = 'incl. VAT · this combination',
	margin,
	marginNegative,
	action,
	loading,
}: CombinationPreviewProps) {
	return (
		/* Two halves when there is room for two, one column when there isn't — and
		   the room that decides it is the card's, not the window's. This panel is
		   rendered full width at the foot of the client's offer and ~280–360px wide
		   in the admin's summary rail, so a viewport breakpoint would split the
		   rail into two 140px columns on the very screens that are widest. `@2xl`
		   is where the full-width card lands at the old `md` viewport, so nothing
		   moves for the callers that were already full width.
		 *
		 * ── Why the container and the query are two elements ──
		 *
		 * They used to be one, and that is not a thing CSS can do: `container-type`
		 * makes an element a query container for its DESCENDANTS, never for itself,
		 * so `@2xl:grid-cols-2` sitting on the `@container` div was quietly asking
		 * the nearest container ABOVE this panel. For a long time there wasn't one,
		 * the query was false, and the panel stacked — the right answer for the
		 * wrong reason. The moment an ancestor became a container (`OfferBatchLayout`
		 * measuring its own width to decide where the rail goes) the query started
		 * reporting that ancestor's ~1140px, and the 280px rail split itself into
		 * two 140px columns with the figures clipped inside them.
		 *
		 * So the container is the outer div and everything that queries it is
		 * inside. Both the split below and the heading's `@2xl:block` now read this
		 * panel's own width, which is what the paragraph above always claimed. */
		<div className="@container">
			<div className="grid gap-px bg-line-1 @2xl:grid-cols-2">
				<div className="bg-surface-sunken px-4 py-3">
					{/* A column heading, so it goes when there are no columns: stacked,
					    the caller's own SubHead is directly above it. */}
					<MicroLabel className="mb-2 hidden @2xl:block">Selected combination</MicroLabel>
					{selection.length === 0 ? (
						<div className="text-sm text-text-4">
							Nothing is in the previewed order.
						</div>
					) : (
						selection.map((entry) => (
							<div key={entry.id} className="flex items-start gap-2 py-1.5">
								<span className="min-w-0 flex-1">
									<span className="flex min-w-0 items-center gap-1.5 text-base font-medium text-text-1">
										<span className="truncate">{entry.name}</span>
									</span>
									{entry.sub && (
										<span className="mt-px block text-2xs text-text-4">
											{entry.sub}
										</span>
									)}
								</span>
								<span className="w-[84px] shrink-0 text-right font-mono text-sm font-medium text-text-1">
									{entry.total ?? '—'}
								</span>
							</div>
						))
					)}
					{excludedNote && (
						<div className="mt-2 text-xs leading-snug text-text-4">{excludedNote}</div>
					)}
				</div>

				<div className="bg-surface-sunken px-4 py-3">
					{/* Only the totals wait. The selection beside them is local state —
					    it is what was clicked — so blanking it too would say the page had
					    lost the picks rather than the price of them. */}
					{loading && <TotalsSkeleton />}

					{!loading &&
						lines.map((line, i) => (
							/* Sections, set apart by space alone — no headings, because each
							   block is two rows that already name themselves. What makes a
							   block is the THING the money is about, not the kind of charge:
							   the goods and shipping the goods, then the sample and shipping
							   the sample. Grouping the two freight lines together instead
							   put a product's shipping next to a sample's and left each of
							   them away from the thing it ships. */
							<div
								key={`${line.label}-${i}`}
								className={cn(
									i > 0 && line.group !== lines[i - 1].group && 'mt-2.5'
								)}
							>
								<div className="flex items-baseline justify-between gap-6 py-0.5">
									<span className="text-sm text-text-3">{line.label}</span>
									<span
										className={cn(
											'font-mono text-sm',
											line.value == null
												? 'text-text-4'
												: line.emphasis === 'fee'
													? 'text-status-warn'
													: line.emphasis === 'discount'
														? 'text-status-success'
														: 'text-text-1'
										)}
									>
										{line.value ?? '—'}
									</span>
								</div>
								{line.note}
							</div>
						))}

					{!loading && (
						<>
							<div className="mt-2 border-t border-line-1 pt-1.5">
								<div className="flex items-baseline justify-between gap-6 py-0.5">
									<span className="text-sm text-text-3">Subtotal</span>
									<span className="font-mono text-sm text-text-1">
										{subtotal ?? '—'}
									</span>
								</div>
								<div className="flex items-baseline justify-between gap-6 py-0.5">
									<span className="text-sm text-text-3">{vatLabel}</span>
									<span className="font-mono text-sm text-text-1">
										{vat ?? '—'}
									</span>
								</div>
							</div>

							<div className="mt-2 flex items-baseline justify-between gap-6 border-t border-line-2 pt-2.5">
								<span className="text-base font-medium text-text-1">Total</span>
								<span className="text-right">
									<span className="block font-mono text-2xl font-medium text-text-1">
										{total ?? '—'}
									</span>
									<span className="mt-px block text-2xs text-text-4">
										{totalNote}
									</span>
								</span>
							</div>
						</>
					)}

					{/* After the Total, behind its own rule. Margin is neither a charge nor
					    a term of one, so anywhere inside the column of things that sum it
					    wore their type and looked like it fed the row beneath it. Past the
					    Total there is nothing left for it to appear to be part of.
					    Admin-only; a client card passes nothing. */}
					{!loading && margin && (
						<div className="mt-2.5 flex items-baseline justify-between gap-6 border-t border-line-1 pt-2.5">
							<span className="text-sm text-text-3">Margin</span>
							{/* A loss is coloured, because it now shares a row style with a
							    profit and nothing else on the line would tell them apart —
							    a minus sign in mono at 14px is one glyph. */}
							<span
								className={cn(
									'font-mono text-sm',
									marginNegative ? 'text-status-warn' : 'text-text-1'
								)}
							>
								{margin}
							</span>
						</div>
					)}

					{/* The action stays put while the figures load. It is the reason the
					    panel is on screen, and a button that comes and goes moves
					    everything under it. Disabling it is the caller's call — it knows
					    whether a total is required to press it. */}
					{action && <div className="mt-2.5 flex justify-end">{action}</div>}
				</div>
			</div>
		</div>
	);
}

/**
 * The totals' shape while the first live price loads.
 *
 * Held rather than left blank: the panel is a column of figures, and a column of
 * dashes reads as a quote that costs nothing rather than one still being priced.
 * Only the first load reaches this — a re-price keeps the previous figures on
 * screen, so the numbers change rather than disappear.
 */
function TotalsSkeleton() {
	return (
		<div className="animate-pulse">
			{[0, 1, 2, 3].map((i) => (
				<div key={i} className="flex items-baseline justify-between gap-6 py-1">
					<span className="h-2.5 w-16 rounded-full bg-surface-fill" />
					<span className="h-2.5 w-20 rounded-full bg-surface-fill" />
				</div>
			))}
			<div className="mt-2 border-t border-line-1 pt-1.5">
				<div className="flex items-baseline justify-between gap-6 py-0.5">
					<span className="h-2.5 w-14 rounded-full bg-surface-fill" />
					<span className="h-2.5 w-20 rounded-full bg-surface-fill" />
				</div>
			</div>
			<div className="mt-2 border-t border-line-2 pt-2.5">
				<div className="flex items-baseline justify-between gap-6">
					<span className="h-3 w-12 rounded-full bg-surface-fill" />
					<span className="h-4 w-24 rounded-full bg-surface-fill" />
				</div>
			</div>
		</div>
	);
}
